import { useEffect, useRef, useState } from "react";
import {
  deleteHighlight,
  downloadMarkdown,
  getBookReview,
  listHighlights,
  setBookReview,
  updateHighlight,
} from "@/services/notesService";
import type { Highlight, HighlightColor } from "@/types/highlight";
import { HIGHLIGHT_COLORS, DEFAULT_TAGS } from "@/types/highlight";
import { askAI, AI_MODES, type AiMode } from "@/services/aiService";
import {
  Recorder,
  transcribeAudio,
  sttSupported,
  saveAudioNote,
  listAudioNotes,
  deleteAudioNote,
} from "@/services/sttService";
import type { AudioNoteRecord } from "@/services/db";
import {
  hasNostrSigner,
  previewEvent,
  publishAnnotation,
  type BookMeta,
} from "@/services/nostrService";

interface Props {
  bookId: string;
  bookTitle: string;
  bookAuthor?: string;
  onClose: () => void;
  onJumpTo: (cfi: string) => void;
  onOpenInsights?: () => void;
}

/**
 * Right-side drawer: the book's notebook. A rating + review at the top, then
 * every highlight — jump to it, recolor, note, tag, delete — and export.
 */
export function NotesPanel({
  bookId,
  bookTitle,
  bookAuthor,
  onClose,
  onJumpTo,
  onOpenInsights,
}: Props) {
  const [items, setItems] = useState<Highlight[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftNote, setDraftNote] = useState("");
  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [rating, setRating] = useState(0);
  const [reviewBody, setReviewBody] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [sharing, setSharing] = useState<Highlight | null>(null);

  const bookMeta: BookMeta = { title: bookTitle, author: bookAuthor };

  const reload = async () => {
    setItems(await listHighlights(bookId));
    const rev = await getBookReview(bookId);
    setRating(rev?.rating ?? 0);
    setReviewBody(rev?.note ?? "");
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  const colorHex = (c: HighlightColor) =>
    HIGHLIGHT_COLORS.find((x) => x.id === c)?.hex ?? "#ffec6b";

  const saveRating = async (r: number) => {
    setRating(r);
    await setBookReview(bookId, r || undefined, reviewBody.trim() || undefined);
  };
  const saveReview = async () => {
    await setBookReview(bookId, rating || undefined, reviewBody.trim() || undefined);
  };

  const saveTags = async (h: Highlight, tags: string[]) => {
    await updateHighlight(h.id, { tags: tags.length ? tags : [] });
    reload();
  };

  const appendVoiceNote = async (h: Highlight, text: string, blob: Blob) => {
    // Keep BOTH: the transcription in the note AND the original audio file.
    if (text) {
      const merged = h.note ? `${h.note} ${text}` : text;
      await updateHighlight(h.id, { note: merged });
    }
    await saveAudioNote(h.id, bookId, blob);
    reload();
  };

  // All tags in use, for the filter row.
  const allTags = Array.from(
    new Set(items.flatMap((h) => h.tags ?? [])),
  ).sort();
  // Browsable/searchable notebook: filter by free text and by an active tag.
  const shown = items.filter((h) => {
    if (tagFilter && !(h.tags ?? []).includes(tagFilter)) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return `${h.text} ${h.note ?? ""} ${(h.tags ?? []).join(" ")} ${h.aiResponse ?? ""} ${h.chapterLabel ?? ""}`
      .toLowerCase()
      .includes(q);
  });

  return (
    <>
      <div className="fixed inset-0 z-20 bg-black/20" onClick={onClose} aria-hidden />
      <div className="pv-surface pv-border pv-fg fixed inset-x-0 top-1/2 z-30 mx-auto flex max-h-[80vh] w-[88vw] max-w-md -translate-y-1/2 flex-col rounded-lg border shadow-2xl">
        <div className="pv-border flex items-center justify-between border-b px-4 py-3">
          <div className="text-base font-semibold">Notebook ({shown.length})</div>
          <div className="flex items-center gap-3 text-sm">
            {onOpenInsights && (
              <button
                onClick={onOpenInsights}
                className="pv-border pv-fg rounded border px-2 py-1"
                title="Book stats & word clouds"
              >
                Insights
              </button>
            )}
            <button
              onClick={() => void downloadMarkdown(bookId, bookTitle, bookAuthor)}
              className="pv-border pv-fg rounded border px-2 py-1"
              disabled={items.length === 0 && rating === 0 && !reviewBody}
            >
              Export .md
            </button>
            <button onClick={onClose} className="pv-fg-soft hover:underline">Close</button>
          </div>
        </div>

        {/* Search + tag filter — makes the notebook browsable. */}
        {items.length > 0 && (
          <div className="pv-border border-b px-3 py-2">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search notes, highlights, tags…"
              className="pv-surface-2 pv-border w-full rounded border px-2 py-1 text-xs"
            />
            {allTags.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {allTags.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTagFilter(tagFilter === t ? null : t)}
                    className={`rounded-full border px-2 py-0.5 text-[11px] ${
                      tagFilter === t ? "pv-accent-bg border-transparent" : "pv-border pv-fg-soft"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Rating + review (book-level annotation) ── */}
        <div className="pv-border border-b px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="pv-fg-soft text-xs font-medium uppercase tracking-wider">
              Your rating
            </span>
            <Stars value={rating} onChange={saveRating} />
          </div>
          {reviewOpen || reviewBody ? (
            <div className="mt-2">
              <textarea
                value={reviewBody}
                onChange={(e) => setReviewBody(e.target.value)}
                onBlur={() => void saveReview()}
                placeholder="Write a review…"
                rows={3}
                className="pv-surface-2 pv-border w-full rounded border px-2 py-1 text-xs"
              />
            </div>
          ) : (
            <button
              onClick={() => setReviewOpen(true)}
              className="pv-fg-soft mt-2 text-xs hover:underline"
            >
              + write a review
            </button>
          )}
          {(rating > 0 || reviewBody.trim()) && (
            <button
              onClick={async () => {
                const rev = await setBookReview(
                  bookId, rating || undefined, reviewBody.trim() || undefined,
                );
                setSharing(rev);
              }}
              className="mt-1 text-xs hover:underline"
              style={{ color: "#f7931a" }}
            >
              ↗ share review
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="pv-fg-soft p-6 text-center text-sm">
            Highlight any text in the book — long-press on mobile, click-and-drag
            on desktop — then pick a color. Highlights, notes, and tags show up
            here and export as Markdown.
          </div>
        ) : (
          <ul className="flex-1 space-y-3 overflow-y-auto p-3 text-sm">
            {shown.length === 0 && (
              <li className="pv-fg-soft py-4 text-center text-xs">No matches.</li>
            )}
            {shown.map((h) => (
              <li key={h.id} className="pv-border rounded border p-2">
                <div className="flex items-start gap-2">
                  <button
                    aria-label="Open in book"
                    onClick={() => onJumpTo(h.cfiRange)}
                    className="flex-1 text-left"
                  >
                    <blockquote
                      className="border-l-4 pl-2 italic"
                      style={{ borderColor: colorHex(h.color) }}
                    >
                      {h.text}
                    </blockquote>
                    {h.chapterLabel && (
                      <div className="pv-fg-soft mt-1 text-[11px]">{h.chapterLabel}</div>
                    )}
                  </button>
                  <ColorPicker
                    value={h.color}
                    onChange={async (c) => {
                      await updateHighlight(h.id, { color: c });
                      reload();
                    }}
                  />
                  <button
                    aria-label="Share"
                    title="Share to the open network"
                    onClick={() => setSharing(h)}
                    className="pv-fg-soft text-xs"
                    style={{ color: h.visibility === "public" ? "#f7931a" : undefined }}
                  >
                    ↗
                  </button>
                  <button
                    aria-label="Delete"
                    onClick={async () => {
                      await deleteHighlight(h.id);
                      reload();
                    }}
                    className="pv-fg-soft text-xs"
                  >
                    ✕
                  </button>
                </div>

                {/* note */}
                {editingId === h.id ? (
                  <div className="mt-2 flex flex-col gap-1">
                    <textarea
                      value={draftNote}
                      onChange={(e) => setDraftNote(e.target.value)}
                      autoFocus
                      placeholder="Add a note…"
                      className="pv-surface-2 pv-border w-full rounded border px-2 py-1 text-xs"
                      rows={3}
                    />
                    <div className="flex justify-end gap-2 text-xs">
                      <button
                        onClick={() => { setEditingId(null); setDraftNote(""); }}
                        className="pv-fg-soft"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          await updateHighlight(h.id, { note: draftNote.trim() || undefined });
                          setEditingId(null);
                          setDraftNote("");
                          reload();
                        }}
                        className="pv-accent-bg rounded px-2 py-0.5 font-semibold"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : h.note ? (
                  <button
                    onClick={() => { setEditingId(h.id); setDraftNote(h.note ?? ""); }}
                    className="pv-fg mt-2 block w-full text-left text-xs"
                  >
                    <span className="pv-fg-soft">Note: </span>
                    {h.note}
                  </button>
                ) : (
                  <button
                    onClick={() => { setEditingId(h.id); setDraftNote(""); }}
                    className="pv-fg-soft mt-2 text-xs hover:underline"
                  >
                    + add note
                  </button>
                )}

                {/* voice note — speak a reaction, transcribed locally; the
                    original audio is kept too and can be played back. */}
                <AudioNotes key={`an-${h.id}-${h.updatedAt}`} highlightId={h.id} />
                <VoiceNote onDone={(t, b) => appendVoiceNote(h, t, b)} />

                {/* tags */}
                <TagEditor tags={h.tags ?? []} onChange={(t) => saveTags(h, t)} />

                {/* AI reading companion */}
                <AICompanion
                  h={h}
                  bookTitle={bookTitle}
                  bookAuthor={bookAuthor}
                  onSaved={reload}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {sharing && (
        <ShareSheet
          h={sharing}
          book={bookMeta}
          onClose={() => setSharing(null)}
          onPublished={() => { setSharing(null); reload(); }}
        />
      )}
    </>
  );
}

function ShareSheet({
  h,
  book,
  onClose,
  onPublished,
}: {
  h: Highlight;
  book: BookMeta;
  onClose: () => void;
  onPublished: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const ev = previewEvent(h, book);
  const signer = hasNostrSigner();

  const publish = async () => {
    setBusy(true);
    setError("");
    try {
      const { accepted } = await publishAnnotation(h, book);
      await updateHighlight(h.id, { visibility: "public" });
      setResult(`Published to ${accepted.length} relay${accepted.length === 1 ? "" : "s"}.`);
      setTimeout(onPublished, 1200);
    } catch (e) {
      setError((e as Error).message || "publish failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} aria-hidden />
      <div className="pv-surface pv-border pv-fg fixed inset-x-0 top-1/2 z-50 mx-auto flex max-h-[80vh] w-[88vw] max-w-md -translate-y-1/2 flex-col rounded-lg border p-4 shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold">Share to the open network</div>
          <button onClick={onClose} className="pv-fg-soft text-sm hover:underline">Close</button>
        </div>

        <p className="pv-fg-soft mt-2 text-xs">
          This is exactly what will be published — nothing else. Your private
          notes, reading activity, and anything not shown here stay on your device.
        </p>

        {/* §13 preview: exactly what leaves the device */}
        <div className="pv-surface-2 pv-border mt-3 max-h-52 overflow-y-auto rounded border p-2 text-xs">
          <div className="pv-fg-soft mb-1 text-[10px] uppercase tracking-wider">
            {ev.kind === 9802 ? "Highlight · NIP-84" : "Review · note"}
          </div>
          {ev.content && <div className="whitespace-pre-wrap">{ev.content}</div>}
          <div className="mt-2 flex flex-wrap gap-1">
            {ev.tags
              .filter((t) => ["t", "book", "comment", "rating"].includes(t[0]))
              .map((t, i) => (
                <span key={i} className="pv-accent-bg rounded-full px-2 py-0.5 text-[10px]">
                  {t[0] === "t" ? `#${t[1]}` : t[0] === "comment" ? `note: ${t[1].slice(0, 40)}` : t[1]}
                </span>
              ))}
          </div>
        </div>

        {result ? (
          <div className="mt-3 text-sm" style={{ color: "#f7931a" }}>{result}</div>
        ) : signer ? (
          <button
            onClick={publish}
            disabled={busy}
            className="pv-accent-bg mt-4 rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {busy ? "Publishing…" : "Publish to Nostr"}
          </button>
        ) : (
          <div className="pv-fg-soft mt-4 text-xs">
            No Nostr signer detected. Install a signer extension (Alby, nos2x) or
            connect your Nostr wallet to publish. Your keys never touch PageVoice —
            the signer holds them and signs on request.
          </div>
        )}
        {error && <div className="mt-2 text-xs text-red-400">{error}</div>}
      </div>
    </>
  );
}

function AudioNotes({ highlightId }: { highlightId: string }) {
  const [notes, setNotes] = useState<AudioNoteRecord[]>([]);
  useEffect(() => {
    let alive = true;
    void listAudioNotes(highlightId).then((n) => { if (alive) setNotes(n); });
    return () => { alive = false; };
  }, [highlightId]);
  if (notes.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {notes.map((n) => (
        <AudioChip
          key={n.id}
          note={n}
          onDelete={async () => {
            await deleteAudioNote(n.id);
            setNotes((cur) => cur.filter((x) => x.id !== n.id));
          }}
        />
      ))}
    </div>
  );
}

function AudioChip({ note, onDelete }: { note: AudioNoteRecord; onDelete: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(note.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [note.blob]);
  return (
    <span
      className="pv-border inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]"
      title={new Date(note.createdAt).toLocaleString()}
    >
      <button
        onClick={() => { if (url) void new Audio(url).play(); }}
        className="pv-fg"
        aria-label="Play voice note"
      >
        ▶ audio
      </button>
      <button onClick={onDelete} className="pv-fg-soft" aria-label="Delete audio">
        ✕
      </button>
    </span>
  );
}

function VoiceNote({ onDone }: { onDone: (text: string, blob: Blob) => void }) {
  const [state, setState] = useState<"idle" | "recording" | "working">("idle");
  const [error, setError] = useState("");
  const recRef = useRef<Recorder | null>(null);

  if (!sttSupported()) return null;

  const start = async () => {
    setError("");
    try {
      const rec = new Recorder();
      await rec.start();
      recRef.current = rec;
      setState("recording");
    } catch {
      setError("mic unavailable");
      setState("idle");
    }
  };
  const stop = async () => {
    const rec = recRef.current;
    if (!rec) return;
    setState("working");
    try {
      const blob = await rec.stop();
      let text = "";
      try {
        text = await transcribeAudio(blob);
      } catch {
        /* transcription failed — still keep the audio */
      }
      onDone(text, blob);
    } catch (e) {
      setError((e as Error).message || "recording failed");
    } finally {
      recRef.current = null;
      setState("idle");
    }
  };

  return (
    <div className="mt-1">
      {state === "idle" && (
        <button onClick={start} className="pv-fg-soft text-xs hover:underline">
          🎤 voice note
        </button>
      )}
      {state === "recording" && (
        <button
          onClick={stop}
          className="rounded px-2 py-0.5 text-xs font-medium"
          style={{ background: "rgba(248,113,113,0.15)", color: "#f87171" }}
        >
          ● stop &amp; transcribe
        </button>
      )}
      {state === "working" && <span className="pv-fg-soft text-xs">transcribing…</span>}
      {error && <span className="ml-2 text-[11px] text-red-400">{error}</span>}
    </div>
  );
}

function AICompanion({
  h,
  bookTitle,
  bookAuthor,
  onSaved,
}: {
  h: Highlight;
  bookTitle: string;
  bookAuthor?: string;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<AiMode | null>(null);
  const [error, setError] = useState("");

  const run = async (mode: AiMode) => {
    setBusy(mode);
    setError("");
    try {
      const answer = await askAI(mode, {
        passage: h.text,
        note: h.note,
        bookTitle,
        bookAuthor,
      });
      await updateHighlight(h.id, { aiResponse: `[${mode}] ${answer}` });
      onSaved();
    } catch (e) {
      setError((e as Error).message || "AI request failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-2">
      {/* Saved AI response — always clearly labelled and visually distinct
          from the reader's own note. */}
      {h.aiResponse && (
        <div
          className="mb-2 rounded-md px-2 py-1.5 text-xs"
          style={{
            background: "rgba(247,147,26,0.08)",
            border: "1px solid rgba(247,147,26,0.3)",
          }}
        >
          <div className="mb-0.5 flex items-center justify-between">
            <span
              className="text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: "#f7931a" }}
            >
              AI companion
            </span>
            <button
              onClick={async () => {
                await updateHighlight(h.id, { aiResponse: undefined });
                onSaved();
              }}
              className="pv-fg-soft text-[10px] hover:underline"
            >
              clear
            </button>
          </div>
          <div className="pv-fg whitespace-pre-wrap">{h.aiResponse}</div>
        </div>
      )}

      {open ? (
        <div className="flex flex-wrap items-center gap-1">
          {AI_MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => run(m.id)}
              disabled={busy !== null}
              title={m.hint}
              className="pv-border pv-fg-soft rounded-full border px-2 py-0.5 text-[11px] hover:opacity-80 disabled:opacity-50"
            >
              {busy === m.id ? "…" : m.label}
            </button>
          ))}
          <button
            onClick={() => setOpen(false)}
            className="pv-fg-soft text-[11px] hover:underline"
          >
            close
          </button>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="pv-fg-soft text-xs hover:underline"
          style={{ color: "#f7931a" }}
        >
          ✦ ask AI
        </button>
      )}
      {error && <div className="mt-1 text-[11px] text-red-400">{error}</div>}
    </div>
  );
}

function Stars({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          onClick={() => onChange(value === n ? 0 : n)}
          className="text-lg leading-none"
          style={{ color: n <= value ? "#f7931a" : "rgba(255,255,255,0.25)" }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function TagEditor({
  tags,
  onChange,
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const toggle = (t: string) =>
    onChange(tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]);

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-1">
        {tags.map((t) => (
          <button
            key={t}
            onClick={() => toggle(t)}
            className="pv-accent-bg rounded-full px-2 py-0.5 text-[11px] font-medium"
            title="Remove tag"
          >
            {t} ✕
          </button>
        ))}
        <button
          onClick={() => setOpen((o) => !o)}
          className="pv-fg-soft rounded-full border border-white/20 px-2 py-0.5 text-[11px] hover:opacity-80"
        >
          {open ? "done" : "＋ tag"}
        </button>
      </div>
      {open && (
        <div className="mt-1.5">
          <div className="flex flex-wrap gap-1">
            {DEFAULT_TAGS.map((t) => {
              const on = tags.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => toggle(t)}
                  className={`rounded-full border px-2 py-0.5 text-[11px] ${
                    on ? "pv-accent-bg border-transparent" : "pv-border pv-fg-soft"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const t = custom.trim();
              if (t && !tags.includes(t)) onChange([...tags, t]);
              setCustom("");
            }}
            className="mt-1.5 flex gap-1"
          >
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="custom tag…"
              className="pv-surface-2 pv-border flex-1 rounded border px-2 py-0.5 text-[11px]"
            />
            <button
              type="submit"
              className="pv-border pv-fg rounded border px-2 py-0.5 text-[11px]"
            >
              add
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function ColorPicker({
  value,
  onChange,
}: {
  value: HighlightColor;
  onChange: (c: HighlightColor) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {HIGHLIGHT_COLORS.map((c) => (
        <button
          key={c.id}
          aria-label={c.label}
          onClick={() => onChange(c.id)}
          className={`h-4 w-4 rounded-full border ${
            value === c.id ? "ring-2 ring-white/70" : "border-white/30"
          }`}
          style={{ background: c.hex }}
        />
      ))}
    </div>
  );
}
